import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { X, History, Download, Trash2, Hash, Clock, AlertTriangle, AudioWaveform, Loader2 } from 'lucide-react';
import Modal from './Modal';
import api from '../api/axios';
import { downloadReadingsAsCsv } from '../utils/csv';
import { LIGHT_PALETTE, DARK_PALETTE } from './CombinedVisualization';
import { Recording, Reading } from './MonitoringDashboard';

interface RecordingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  recordings: Recording[];
  sensorLabel?: string;
  canDelete: boolean;
  onChange?: () => void;
}

// Vista de leitura das gravações de UM sensor em tempo real (a lista já vem filtrada
// pelo MonitoringDashboard - cada sensor só mostra as suas próprias, não as do projeto
// todo misturadas): grelha de gráficos pequenos (mesmo estilo dos cartões da
// Visualização conjunta), com download de CSV e remoção por gravação. Sem edição - a
// gravação em si faz-se no MonitoringDashboard, ligada ao sensor que estava a ser visto.
const RecordingsModal: React.FC<RecordingsModalProps> = ({ isOpen, onClose, projectId, recordings, sensorLabel, canDelete, onChange }) => {
  const [confirmDelete, setConfirmDelete] = useState<Recording | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showSpectrum, setShowSpectrum] = useState<Record<string, boolean>>({});

  // As leituras de cada gravação já não vêm com o resto do projeto - só se busca
  // "data" quando o modal abre de facto (uma gravação pode ir até 24h de amostras;
  // ver a nota em auth-service/main.py:create_recording).
  const [recordingData, setRecordingData] = useState<Record<string, Reading[]>>({});
  const [loadingIds, setLoadingIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) return;
    const missing = recordings.filter(r => !recordingData[r.id] && !loadingIds[r.id]);
    if (missing.length === 0) return;
    setLoadingIds(prev => ({ ...prev, ...Object.fromEntries(missing.map(r => [r.id, true])) }));
    missing.forEach(async (rec) => {
      try {
        const res = await api.get(`/projects/${projectId}/recordings/${rec.id}/data`);
        setRecordingData(prev => ({ ...prev, [rec.id]: res.data.data || [] }));
      } catch {
        setRecordingData(prev => ({ ...prev, [rec.id]: [] }));
      } finally {
        setLoadingIds(prev => ({ ...prev, [rec.id]: false }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, recordings, projectId]);

  if (!isOpen) return null;

  const isDarkMode = document.documentElement.classList.contains('dark');
  const palette = isDarkMode ? DARK_PALETTE : LIGHT_PALETTE;

  const chartOptions = (columns: string[], unit?: string): ApexOptions => ({
    chart: { type: 'line', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, background: 'transparent', foreColor: isDarkMode ? '#94a3b8' : '#64748b' },
    colors: palette,
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2, lineCap: 'round' },
    xaxis: {
      type: 'datetime',
      labels: { style: { fontWeight: 700, colors: isDarkMode ? '#94a3b8' : '#64748b' }, datetimeUTC: false, datetimeFormatter: { hour: 'HH:mm', minute: 'HH:mm:ss' } },
      axisBorder: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
      axisTicks: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
    },
    yaxis: { labels: { style: { fontWeight: 800, colors: [isDarkMode ? '#94a3b8' : '#64748b'] }, formatter: (v) => (v == null ? '' : Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 1 })) } },
    grid: { borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)', yaxis: { lines: { show: true } }, xaxis: { lines: { show: false } }, padding: { top: 0, right: 12, bottom: 0, left: 8 } },
    tooltip: { theme: isDarkMode ? 'dark' : 'light', shared: true, intersect: false, x: { format: 'dd MMM HH:mm:ss' }, y: { formatter: (v) => (v == null ? '—' : `${v.toFixed(2)} ${unit || ''}`.trim()) } },
    markers: { size: 0, strokeWidth: 2, hover: { size: 5 }, strokeColors: isDarkMode ? '#0b0f1a' : '#fff' },
    legend: { position: 'bottom', horizontalAlign: 'center', fontWeight: 600, offsetY: 4, labels: { useSeriesColors: false }, markers: { strokeWidth: 0 } as any },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
  });

  const seriesFor = (rec: Recording, data: Reading[]) => {
    const columns = rec.selectedColumns?.length ? rec.selectedColumns : Object.keys(data[0] || {}).filter(k => k !== 'date');
    return columns.map(col => ({
      name: col,
      data: data.map(d => ({ x: new Date(d.date).getTime(), y: d[col] })),
    }));
  };

  // Espectro de amplitude (FFT) por canal - eixo dos X em Hz, não datetime.
  const fftChartOptions = (unit?: string): ApexOptions => ({
    chart: { type: 'area', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, background: 'transparent', foreColor: isDarkMode ? '#94a3b8' : '#64748b' },
    colors: palette,
    dataLabels: { enabled: false },
    stroke: { curve: 'straight', width: 1.5 },
    fill: { type: 'gradient', gradient: { opacityFrom: 0.35, opacityTo: 0 } },
    xaxis: {
      type: 'numeric',
      title: { text: 'Frequência (Hz)', style: { fontSize: '10px', fontWeight: 700, color: isDarkMode ? '#64748b' : '#94a3b8' } },
      labels: { style: { fontWeight: 700, colors: isDarkMode ? '#94a3b8' : '#64748b' }, formatter: (v) => Number(v).toFixed(0) },
      axisBorder: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
      axisTicks: { show: true, color: isDarkMode ? '#334155' : '#e2e8f0' },
    },
    yaxis: { labels: { show: false } },
    grid: { borderColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.05)', yaxis: { lines: { show: false } }, xaxis: { lines: { show: false } }, padding: { top: 0, right: 12, bottom: 0, left: 8 } },
    tooltip: { theme: isDarkMode ? 'dark' : 'light', shared: true, intersect: false, x: { formatter: (v) => `${Number(v).toFixed(1)} Hz` }, y: { formatter: (v) => (v == null ? '—' : `${v.toFixed(3)} ${unit || ''}`.trim()) } },
    markers: { size: 0 },
    legend: { position: 'bottom', horizontalAlign: 'center', fontWeight: 600, offsetY: 0, labels: { useSeriesColors: false }, markers: { strokeWidth: 0 } as any },
    theme: { mode: isDarkMode ? 'dark' : 'light' },
  });

  const fftSeriesFor = (rec: Recording) => {
    if (!rec.fft) return [];
    return Object.entries(rec.fft.channels).map(([col, spectrum]) => ({
      name: col,
      data: spectrum.frequencies.map((f, i) => ({ x: f, y: spectrum.magnitudes[i] })),
    }));
  };

  const formatDuration = (sec?: number) => {
    if (!sec) return '—';
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.round(sec / 60)}min`;
    return `${(sec / 3600).toFixed(1)}h`;
  };

  const handleDownload = (rec: Recording, data: Reading[]) => {
    const columns = rec.selectedColumns?.length ? rec.selectedColumns : Object.keys(data[0] || {}).filter(k => k !== 'date');
    downloadReadingsAsCsv(data, columns, rec.titulo);
  };

  const handleDelete = async (rec: Recording) => {
    setConfirmDelete(null);
    setDeleting(true);
    try {
      await api.delete(`/projects/${projectId}/recordings/${rec.id}`);
      onChange && onChange();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[3200] flex items-center justify-center p-4 bg-[#05070a]/90 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#161b26] rounded-3xl border border-slate-200 dark:border-slate-800 max-w-5xl w-full max-h-[88vh] overflow-hidden shadow-2xl animate-in zoom-in duration-300 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-600/10 text-blue-600 rounded-xl"><History size={18} /></div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Gravações{sensorLabel ? ` · ${sensorLabel}` : ''}</h3>
              <p className="text-xs text-slate-400">Snapshots guardados deste sensor em tempo real</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"><X size={20} className="text-slate-400" /></button>
        </div>

        <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
          {recordings.length === 0 ? (
            <div className="border border-dashed border-slate-200 dark:border-slate-700 rounded-2xl p-10 text-center">
              <History className="mx-auto text-slate-300 dark:text-slate-600 mb-3" size={32} />
              <p className="text-slate-500 dark:text-slate-400 font-medium text-sm">Ainda não há gravações deste sensor.</p>
              <p className="text-xs text-slate-400 mt-1">Usa o botão "Gravar" para começares uma.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {[...recordings].reverse().map(rec => {
                const isAlert = rec.tipo === 'alerta';
                const limiar = rec.triggerThreshold;
                const limiarTexto = limiar
                  ? [limiar.limiteInferior != null ? `min ${limiar.limiteInferior}` : null, limiar.limiteSuperior != null ? `máx ${limiar.limiteSuperior}` : null].filter(Boolean).join(' · ')
                  : null;
                const data = recordingData[rec.id];
                const isLoading = !data && loadingIds[rec.id];
                const readingCount = rec.n_readings ?? data?.length ?? 0;
                return (
                <div key={rec.id} className={`bg-slate-50 dark:bg-slate-900/50 rounded-2xl border p-5 transition-colors ${isAlert ? 'border-amber-300 dark:border-amber-700/60 ring-1 ring-amber-200/60 dark:ring-amber-800/30' : 'border-slate-200 dark:border-slate-700'}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {isAlert && (
                          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400 shrink-0">
                            <AlertTriangle size={11} /> Alerta
                          </span>
                        )}
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white truncate" title={rec.titulo}>{rec.titulo}</h4>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-0.5">
                        <span className="flex items-center gap-1"><Hash size={11} /> {readingCount.toLocaleString()} leituras</span>
                        <span className="flex items-center gap-1"><Clock size={11} /> {formatDuration(rec.requestedDurationSec)}</span>
                      </div>
                      {isAlert && rec.triggerColumn && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">
                          Gatilho: canal <strong>{rec.triggerColumn}</strong> = {rec.triggerValue?.toFixed?.(3) ?? rec.triggerValue}{rec.unit ? ` ${rec.unit}` : ''}
                          {limiarTexto ? ` (limiar: ${limiarTexto})` : ''}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {rec.fft && (
                        <button
                          onClick={() => setShowSpectrum(s => ({ ...s, [rec.id]: !s[rec.id] }))}
                          className={`p-2 rounded-lg transition-all ${showSpectrum[rec.id] ? 'text-blue-600 bg-white dark:bg-slate-800' : 'text-slate-400 hover:text-blue-600 hover:bg-white dark:hover:bg-slate-800'}`}
                          title="Ver espectro de frequência (FFT)"
                        >
                          <AudioWaveform size={15} />
                        </button>
                      )}
                      <button onClick={() => data && handleDownload(rec, data)} disabled={!data} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-white dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:text-slate-400 rounded-lg transition-all" title="Descarregar CSV">
                        <Download size={15} />
                      </button>
                      {canDelete && (
                        <button onClick={() => setConfirmDelete(rec)} disabled={deleting} className="p-2 text-slate-400 hover:text-red-500 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all" title="Apagar gravação">
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>

                  {isLoading ? (
                    <div className="h-[220px] flex items-center justify-center text-sm text-slate-400 gap-2">
                      <Loader2 size={16} className="animate-spin" /> A carregar leituras...
                    </div>
                  ) : data && data.length > 0 ? (
                    <Chart options={chartOptions(rec.selectedColumns || [], rec.unit)} series={seriesFor(rec, data)} type="line" height={220} />
                  ) : (
                    <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">Sem dados.</div>
                  )}

                  {rec.fft && showSpectrum[rec.id] && (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                      <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1 flex items-center gap-1"><AudioWaveform size={11} /> Espectro de frequência (FFT)</p>
                      <Chart options={fftChartOptions(rec.unit)} series={fftSeriesFor(rec)} type="area" height={160} />
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={!!confirmDelete}
        title="Apagar gravação"
        message={`Tens a certeza que queres apagar "${confirmDelete?.titulo}"? Esta ação não pode ser desfeita.`}
        type="danger"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
      />
    </div>
  );
};

export default RecordingsModal;
